import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { Skeleton } from "./Skeleton";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Card> compound component.
// --ds-* derives from config.css (Stage 2), so the CSS in globals.css's
// "Stage 5: Card" block reads the exact same token references the vendor's
// Card-W67PDCDA.css did — a visual no-op, not a redesign. Dropped the dark-
// mode neon glow: this app already force-disables it repo-wide
// (`.ds-card, .ds-stat-card, .ds-chip { box-shadow: none !important }` in
// globals.css, predating this stage), so nothing here needs an equivalent
// override to reproduce.
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** d2d-DetectHub eval F10: "dense" pads the header, body and footer 16px
      instead of 24px, for cards in a grid or a narrow column, where 24px of
      chrome on every side is more card than content. */
  density?: "default" | "dense";
}

const CardImpl = forwardRef<HTMLDivElement, CardProps>(function Card({ className, children, density = "default", ...rest }, ref) {
  return (
    <div ref={ref} className={cn("rc-card", className)} data-density={density === "dense" ? "dense" : undefined} {...rest}>
      {children}
    </div>
  );
});

type CardSectionProps = HTMLAttributes<HTMLDivElement>;

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Card title. Renders the standard title treatment; omit and pass children to hand-roll one. */
  title?: ReactNode;
  /** One muted line under the title — the reference's "Shows API usage over time…". */
  description?: ReactNode;
  /** Controls pinned to the right of the header row. */
  actions?: ReactNode;
  /**
   * Hairline under the header. On by default because most cards label
   * something structured below them; pass false for a card that reads as
   * one continuous thought (a metric, a chart), which is the split the
   * reference design makes.
   */
  divider?: boolean;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { className, children, title, description, actions, divider = true, ...rest },
  ref
) {
  // Two ways in, on purpose. `title`/`description`/`actions` is the shape new
  // code should use and the one that guarantees every card agrees. Plain
  // children is what all 86 existing headers pass — each with its own
  // `flex items-center justify-between` wrapper — and those keep rendering
  // exactly as before via `> *:only-child { flex: 1 }` in globals.css. Both
  // paths land in the same flex row, so a header can also mix them.
  const structured = title != null || description != null || actions != null;
  return (
    <div
      ref={ref}
      className={cn("rc-card__header", className)}
      data-divider={divider ? undefined : "false"}
      {...rest}
    >
      {structured ? (
        <>
          <div className="rc-card__heading">
            {title != null ? <h3 className="rc-card__title">{title}</h3> : null}
            {description != null ? <p className="rc-card__desc">{description}</p> : null}
          </div>
          {actions != null ? <div className="rc-card__actions">{actions}</div> : null}
        </>
      ) : null}
      {children}
    </div>
  );
});

/** Tiny uppercase column labels for a list or table inside a card. */
const CardColumnHeader = forwardRef<HTMLDivElement, CardSectionProps>(function CardColumnHeader(
  { className, children, ...rest },
  ref
) {
  return (
    <div ref={ref} className={cn("rc-card__colhead", className)} {...rest}>
      {children}
    </div>
  );
});

const CardBody = forwardRef<HTMLDivElement, CardSectionProps>(function CardBody({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={cn("rc-card__body", className)} {...rest}>
      {children}
    </div>
  );
});

const CardFooter = forwardRef<HTMLDivElement, CardSectionProps>(function CardFooter({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={cn("rc-card__footer", className)} {...rest}>
      {children}
    </div>
  );
});

export interface CardActionProps extends HTMLAttributes<HTMLElement> {
  /** Renders an anchor instead of a button. */
  href?: string;
}

/**
 * The full-bleed footer action — the reference's "See more". Brings its own
 * footer band, because the band exists only to hold it: a padded footer with
 * a button inside floats the control away from the card edge, which is the
 * one thing this pattern is not.
 */
const CardAction = forwardRef<HTMLElement, CardActionProps>(function CardAction(
  { className, children, href, ...rest },
  ref
) {
  const inner = href ? (
    <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={cn("rc-card__action", className)} {...rest}>
      {children}
    </a>
  ) : (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      className={cn("rc-card__action", className)}
      {...(rest as HTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  );
  return <div className="rc-card__footer rc-card__footer--action">{inner}</div>;
});

export interface CardSkeletonProps {
  /** Number of body text rows. Default 3. */
  rows?: number;
  /** SR label override. Default "Loading". */
  label?: string;
}

function CardSkeleton({ rows = 3, label = "Loading" }: CardSkeletonProps) {
  return (
    <div role="status" aria-busy aria-label={label} className="rc-card-skeleton">
      <Skeleton variant="text" height="1.5em" width="60%" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="text" height="1em" width={i === rows - 1 ? "70%" : "100%"} />
      ))}
    </div>
  );
}

type CardCompound = typeof CardImpl & {
  Header: typeof CardHeader;
  ColumnHeader: typeof CardColumnHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
  Action: typeof CardAction;
  Skeleton: typeof CardSkeleton;
};

const Card = CardImpl as CardCompound;
Card.Header = CardHeader;
Card.ColumnHeader = CardColumnHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Action = CardAction;
Card.Skeleton = CardSkeleton;
Card.displayName = "Card";

export { Card };
